'use client'

import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import { useActionState, useEffect, useRef, useState } from 'react'
import { Check, X } from 'lucide-react'

import { resendVerificationEmail, signUp, verifyEmailCode } from '@/app/actions/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { resolveAuthLanding, resolveSignUpLanding } from '@/constants/authRoutes'
import { getSignUpMobileRequirements } from '@/utilities/signUpMobileValidation'

import { AuthDivider } from './AuthDivider'
import { CodeVerificationForm } from './CodeVerificationForm'
import { OAuthSection } from './OAuthSection'

import type { AuthResult, VerifyEmailResult } from '@/app/actions/auth'

const titleClass = 'text-2xl font-semibold font-family-body text-foreground'
const descriptionClass =
  'text-sm text-muted-foreground flex items-center justify-center gap-2 font-family-body'

function getPasswordRequirements(password: string): Array<{ message: string; met: boolean }> {
  return [
    { message: 'At least 10 characters', met: password.length >= 10 },
    { message: 'At least one lowercase letter', met: /[a-z]/.test(password) },
    { message: 'At least one uppercase letter', met: /[A-Z]/.test(password) },
    {
      message: 'At least one special character',
      met: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
    },
  ]
}

const SignInLink = ({
  redirectTo,
  onSwitchToSignIn,
}: {
  redirectTo: string | null
  onSwitchToSignIn?: () => void
}) => {
  const searchParams = useSearchParams()
  const resolvedRedirectTo = redirectTo ?? searchParams.get('redirectTo')
  const href = resolvedRedirectTo
    ? `/sign-in?redirectTo=${encodeURIComponent(resolvedRedirectTo)}`
    : '/sign-in'

  if (onSwitchToSignIn) {
    return (
      <div className="m-auto w-fit text-center">
        <Button
          type="button"
          variant="link"
          className="text-sm text-muted-foreground transition-colors hover:text-primary"
          onClick={onSwitchToSignIn}
        >
          Already have an account? <span className="font-medium text-primary">Sign in</span>
        </Button>
      </div>
    )
  }

  return (
    <div className="m-auto w-fit text-center">
      <Button
        type="button"
        variant="link"
        className="text-sm text-muted-foreground transition-colors hover:text-primary"
        asChild
      >
        <Link href={href}>
          Already have an account? <span className="font-medium text-primary">Sign in</span>
        </Link>
      </Button>
    </div>
  )
}

const EmailVerification = ({
  email,
  variant = 'page',
  redirectTo,
  onAuthenticated,
}: {
  email: string
  variant?: 'page' | 'modal'
  redirectTo: string | null
  onAuthenticated?: () => void | Promise<void>
}) => {
  const router = useRouter()
  const handledSuccessRef = useRef(false)

  const [verifyState, verifyAction, isPending] = useActionState<VerifyEmailResult | null, FormData>(
    verifyEmailCode,
    null,
  )
  const [isResendLoading, setIsResendLoading] = useState(false)
  const [resendState, setResendState] = useState<VerifyEmailResult | null>(null)

  useEffect(() => {
    if (!verifyState?.success || handledSuccessRef.current) return
    handledSuccessRef.current = true
    if (variant === 'modal' && onAuthenticated) {
      void onAuthenticated()
      return
    }
    router.replace(resolveAuthLanding(verifyState.redirectTo ?? redirectTo))
  }, [onAuthenticated, redirectTo, router, variant, verifyState?.redirectTo, verifyState?.success])

  const handleResendCode = async () => {
    setIsResendLoading(true)
    setResendState(null)
    try {
      const result = await resendVerificationEmail()
      setResendState(result)
    } finally {
      setIsResendLoading(false)
    }
  }

  const containerClass =
    variant === 'modal'
      ? 'mx-auto w-full space-y-6 p-6'
      : 'mx-auto w-full space-y-6 rounded-2xl border border-border p-6'

  return (
    <div className={containerClass}>
      <div className="space-y-2 text-center">
        <h1 className={titleClass}>Verify your email</h1>
        <p className={descriptionClass}>
          <span className="block">
            We sent a verification code to {email || 'your email'}. Enter it below to continue.
          </span>
        </p>
      </div>
      <form action={verifyAction} className="space-y-4">
        <input type="hidden" name="redirectTo" value={redirectTo ?? ''} />
        <CodeVerificationForm
          mode="verifyEmail"
          isPending={isPending}
          codeFormError={verifyState?.error}
          onResendCode={handleResendCode}
          isResendLoading={isResendLoading}
          resendState={resendState}
          asFormContents
        />
      </form>
    </div>
  )
}

const SignUpCredentials = ({
  onSignUpSuccess,
  variant = 'page',
  redirectTo,
  onSwitchToSignIn,
}: {
  onSignUpSuccess: (email: string) => void
  variant?: 'page' | 'modal'
  redirectTo: string | null
  onSwitchToSignIn?: () => void
}) => {
  const signInHref = redirectTo
    ? `/sign-in?redirectTo=${encodeURIComponent(redirectTo)}`
    : '/sign-in'

  const [signUpState, signUpAction, isPending] = useActionState<AuthResult | null, FormData>(
    signUp,
    null,
  )
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [mobile, setMobile] = useState('')
  const [password, setPassword] = useState('')

  const passwordRequirements = getPasswordRequirements(password)
  const passwordMeetsRequirements = passwordRequirements.every((r) => r.met)
  const mobileRequirements = getSignUpMobileRequirements(mobile)
  const mobileMeetsRequirements =
    mobileRequirements.length === 0 || mobileRequirements.every((r) => r.met)

  const canSubmit =
    firstName &&
    lastName &&
    email &&
    password &&
    passwordMeetsRequirements &&
    mobileMeetsRequirements

  useEffect(() => {
    if (signUpState?.needsEmailVerification) {
      onSignUpSuccess(email)
    }
  }, [signUpState?.needsEmailVerification, email, onSignUpSuccess])

  const containerClass =
    variant === 'modal'
      ? 'mx-auto w-full space-y-6 p-6'
      : 'mx-auto w-full space-y-6 rounded-2xl border border-border p-6'

  return (
    <div className={containerClass}>
      <div className="space-y-2 text-center">
        <h1 className={titleClass}>Create your account</h1>
        <p className={descriptionClass}>
          <span className="block">Enter your details to join the MAHA movement</span>
        </p>
      </div>
      <OAuthSection redirectTo={redirectTo} />
      <AuthDivider text="Or" />
      <form action={signUpAction} className="space-y-4">
        <input type="hidden" name="redirectTo" value={redirectTo ?? ''} />
        <div className="space-y-2">
          <Label htmlFor="firstName">First Name</Label>
          <Input
            id="firstName"
            name="firstName"
            type="text"
            placeholder="John"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            disabled={isPending}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="lastName">Last Name</Label>
          <Input
            id="lastName"
            name="lastName"
            type="text"
            placeholder="Doe"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            disabled={isPending}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isPending}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="mobile">Mobile Number</Label>
          <Input
            id="mobile"
            name="mobile"
            type="tel"
            placeholder="123-456-7890"
            value={mobile}
            onChange={(e) => setMobile(e.target.value)}
            disabled={isPending}
            aria-invalid={mobileRequirements.length > 0 && !mobileMeetsRequirements}
          />
          {mobile.trim() !== '' && (
            <ul className="space-y-0.5 text-xs text-muted-foreground" aria-live="polite">
              {mobileRequirements.map((req) => (
                <li
                  key={req.message}
                  className={
                    req.met
                      ? 'flex items-center gap-2 text-green-600'
                      : 'flex items-center gap-2 text-red-600'
                  }
                >
                  {req.met ? <Check className="size-4" /> : <X className="size-4" />}
                  {req.message}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            name="password"
            type="password"
            placeholder="••••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={10}
            disabled={isPending}
          />
          {password && (
            <>
              <ul className="space-y-0.5 text-xs text-muted-foreground" aria-live="polite">
                {passwordRequirements.map((req) => (
                  <li
                    key={req.message}
                    className={
                      req.met
                        ? 'flex items-center gap-2 text-green-600'
                        : 'flex items-center gap-2 text-red-600'
                    }
                  >
                    {req.met ? <Check className="size-4" /> : <X className="size-4" />}
                    {req.message}
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-xs text-muted-foreground">
                Avoid common passwords. Use uncommon words, capitalize more than the first letter,
                and avoid obvious substitutions (e.g. &quot;@&quot; for &quot;a&quot;).
              </p>
            </>
          )}
        </div>
        {signUpState?.errorCode === 'email_not_available' ? (
          <div
            className="rounded-md bg-meltwater-50 p-3 text-center text-sm text-meltwater-600"
            role="alert"
          >
            An account with this email has already been created. Please{' '}
            {onSwitchToSignIn ? (
              <button
                type="button"
                onClick={onSwitchToSignIn}
                className="font-medium text-meltwater-600 underline underline-offset-2 hover:text-meltwater-800"
              >
                sign in
              </button>
            ) : (
              <Link
                href={signInHref}
                className="font-medium text-meltwater-600 underline underline-offset-2 hover:text-meltwater-800"
              >
                sign in
              </Link>
            )}{' '}
            or{' '}
            {onSwitchToSignIn ? (
              <button
                type="button"
                onClick={onSwitchToSignIn}
                className="font-medium text-meltwater-600 underline underline-offset-2 hover:text-meltwater-800"
              >
                reset your password
              </button>
            ) : (
              <Link
                href={signInHref}
                className="font-medium text-meltwater-600 underline underline-offset-2 hover:text-meltwater-800"
              >
                reset your password
              </Link>
            )}
            .
          </div>
        ) : (
          signUpState?.error && (
            <div className="rounded-md bg-red-50 p-3 text-center text-sm text-red-600">
              {signUpState.error}
            </div>
          )
        )}
        <Button
          type="submit"
          variant="accent"
          className="w-full"
          disabled={isPending || !canSubmit}
        >
          {isPending ? 'Loading...' : 'Sign up'}
        </Button>
        <p className="text-[10px]">
          By providing your telephone number and email, you consent to receive recurring calls,
          texts, and emails from MAHA Action, including prerecorded messages and via automated
          methods, regarding our advocacy, campaigns, and events. Consent is not a condition of any
          donation or membership. Msg & data rates may apply. Msg frequency varies (typically 4-8
          messages per month). We do not share your phone number or SMS opt-in data with third
          parties for their marketing purposes. For texts, reply STOP to opt out, HELP for help. For
          emails, click the &quot;unsubscribe&quot; link in the footer of any email if you no longer
          wish to receive them. View our{' '}
          <Link href="/terms-of-service" target="_blank">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link href="/privacy-policy" target="_blank">
            Privacy Policy
          </Link>{' '}
          for more info.
        </p>
      </form>
      <AuthDivider />
      <SignInLink redirectTo={redirectTo} onSwitchToSignIn={onSwitchToSignIn} />
    </div>
  )
}

type SignUpFormProps = {
  variant?: 'page' | 'modal'
  redirectTo?: string | null
  onSwitchToSignIn?: () => void
  onAuthenticated?: () => void | Promise<void>
}

const SignUpForm = ({
  variant = 'page',
  redirectTo: propRedirectTo,
  onSwitchToSignIn,
  onAuthenticated,
}: SignUpFormProps) => {
  const searchParams = useSearchParams()
  const redirectTo = resolveSignUpLanding({
    redirectTo: propRedirectTo ?? searchParams.get('redirectTo'),
  })

  const [pendingVerificationEmail, setPendingVerificationEmail] = useState<string | null>(null)

  if (pendingVerificationEmail) {
    return (
      <EmailVerification
        email={pendingVerificationEmail}
        variant={variant}
        redirectTo={redirectTo}
        onAuthenticated={onAuthenticated}
      />
    )
  }

  return (
    <SignUpCredentials
      onSignUpSuccess={setPendingVerificationEmail}
      variant={variant}
      redirectTo={redirectTo}
      onSwitchToSignIn={onSwitchToSignIn}
    />
  )
}

export default SignUpForm
